import { type DefaultFrame, type GlslSpec, type NodeId } from 'web';
import { HandleType } from '@/enums/handle-type.enum';
import { generateCustomGlslSpec, type GeneratedGlslResponse } from '@/api/customGlsl.api';
import {
  DEFAULT_CUSTOM_GLSL_DEFAULT_FRAME,
  type CustomGlslData,
  type GlslImageReference,
  type StoredGlslImageReference,
} from '@/types/node';
import { FlowGraph } from '../FlowGraph';
import { ParameterView } from './ParameterView/ParameterView';

export const MAX_CUSTOM_GLSL_DURATION_SECONDS = 30;

export const MAX_CUSTOM_GLSL_DIMENSION = 2048;

export const CUSTOM_GLSL_FPS = 24;

export { DEFAULT_CUSTOM_GLSL_DEFAULT_FRAME };

export class CustomGlslView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  setGlslSpec(spec: GlslSpec) {
    // Regeneration preserves wiring for uniforms whose id survives (see setCustomGlslSpec). For
    // uniforms the new shader drops, collapse any that were exposed first, otherwise their input
    // handle and the edge feeding it would be left dangling.
    this.collapseDroppedExposedParameters(spec);
    this.graph.wasmEdit((wasm) => wasm.setCustomGlslSpec(this.nodeId, spec), false);
    this.graph.updateNodeOutputs(this.nodeId, false);
  }

  private collapseDroppedExposedParameters(spec: GlslSpec) {
    const parameterView = new ParameterView(this.graph, this.nodeId);
    const survivingIds = new Set(spec.fields.map((field) => field.id));
    for (const key of parameterView.getParameterIds()) {
      if (!survivingIds.has(key) && parameterView.isParameterExposed(key)) {
        parameterView.collapseParameter(key);
      }
    }
  }

  /** The type feeding the node's file input, or `undefined` when nothing resolvable is wired. */
  inputType(): HandleType | undefined {
    return this.graph.nodeInputType(this.nodeId, 'file');
  }

  async generate(
    prompt: string,
    imageReferences: GlslImageReference[],
    signal?: AbortSignal,
  ): Promise<GeneratedGlslResponse> {
    const inputType = this.inputType() === HandleType.Video ? 'video' : 'image';
    const spec = await generateCustomGlslSpec({ inputType, prompt, imageReferences, signal });
    // The node may have been deleted while the request was in flight.
    if (!this.graph.store.getNodeNullable(this.nodeId)) {
      return spec;
    }
    this.setGlslSpec({ ...spec, prompt });
    if (spec.name) {
      this.graph.renameNodeDisplayName(this.nodeId, spec.name);
    }
    return spec;
  }

  getImageReferences(): GlslImageReference[] {
    return toGlslImageReferences(this.graph.store.getNodeData<CustomGlslData>(this.nodeId).imageReferences);
  }

  setImageReferences(refs: GlslImageReference[]): void {
    if (refsEqual(this.getImageReferences(), refs)) return;
    // No asset id means nothing to add, so keep the legacy bare-URL shape.
    const stored: StoredGlslImageReference[] = refs.map((ref) => (ref.blobstore ? ref : ref.url));
    this.graph.edit(() =>
      this.graph.store.updateNodeData<CustomGlslData>(this.nodeId, () => ({ imageReferences: stored })),
    );
  }

  getDefaultFrame(): DefaultFrame {
    const stored = this.graph.store.getNodeData<CustomGlslData>(this.nodeId).defaultFrame;
    if (!stored) return DEFAULT_CUSTOM_GLSL_DEFAULT_FRAME;
    return stored.lockAspectRatio == null ? { ...stored, lockAspectRatio: true } : stored;
  }

  setDefaultFrame(frame: DefaultFrame, ongoing: boolean = false): void {
    const clamped: DefaultFrame = {
      ...frame,
      width: clampDimension(frame.width),
      height: clampDimension(frame.height),
      duration: clampDuration(frame.duration),
    };
    this.graph.wasmEdit((wasm) => wasm.setCustomGlslDefaultFrame(this.nodeId, clamped), ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
  }

  setDefaultFrameWidth(value: number, ongoing: boolean = false): void {
    const frame = this.getDefaultFrame();
    if (!frame.lockAspectRatio) {
      this.setDefaultFrame({ ...frame, width: value }, ongoing);
      return;
    }
    const ratio = frame.width / frame.height;
    let width = clampDimension(value);
    let height = width / ratio;
    if (height > MAX_CUSTOM_GLSL_DIMENSION) {
      height = MAX_CUSTOM_GLSL_DIMENSION;
      width = height * ratio;
    }
    this.setDefaultFrame({ ...frame, width, height }, ongoing);
  }

  setDefaultFrameHeight(value: number, ongoing: boolean = false): void {
    const frame = this.getDefaultFrame();
    if (!frame.lockAspectRatio) {
      this.setDefaultFrame({ ...frame, height: value }, ongoing);
      return;
    }
    const ratio = frame.width / frame.height;
    let height = clampDimension(value);
    let width = height * ratio;
    if (width > MAX_CUSTOM_GLSL_DIMENSION) {
      width = MAX_CUSTOM_GLSL_DIMENSION;
      height = width / ratio;
    }
    this.setDefaultFrame({ ...frame, width, height }, ongoing);
  }

  toggleDefaultFrameLockAspectRatio(): void {
    const frame = this.getDefaultFrame();
    this.setDefaultFrame({ ...frame, lockAspectRatio: !frame.lockAspectRatio });
  }
}

function clampDimension(value: number): number {
  return Math.min(MAX_CUSTOM_GLSL_DIMENSION, Math.max(1, Math.round(value)));
}

function clampDuration(value: number): number {
  const bounded = Math.min(MAX_CUSTOM_GLSL_DURATION_SECONDS, Math.max(0, value));
  return Math.round(bounded * CUSTOM_GLSL_FPS) / CUSTOM_GLSL_FPS;
}

export function toGlslImageReferences(stored: StoredGlslImageReference[] = []): GlslImageReference[] {
  return stored.map((ref) => (typeof ref === 'string' ? { url: ref } : ref));
}

function refsEqual(a: GlslImageReference[], b: GlslImageReference[]): boolean {
  return a.length === b.length && a.every((ref, i) => ref.url === b[i].url && ref.blobstore?.id === b[i].blobstore?.id);
}
